import {Button, Card, DataStatus, Form, FormButton, FormField, Label} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {isNil} from 'ramda';

import {useGetManufacturersQuery, useLazySearchSupplierPartsQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {
  getOptionsFromCustomersAsSupplier,
  getOptionsFromManufacturers,
  getSearchTypeOptions,
  useGetSupplierCustomersQuery,
} from '@dms/shared';

import {
  supplierPartsSearchRequestSchema,
  type SupplierPartsSearchResponse,
} from '@prompt-bridge/shared/validations';

import {EMPTY_PLACEHOLDER, suffixTestId, type RequiredTestIdProps} from 'shared/web';

export type SupplierCatalogSelection = SupplierPartsSearchResponse['parts'][number] & {
  manufacturerLabel: string;
  supplierLabel: string;
};

type SupplierCatalogPickerProps = RequiredTestIdProps & {
  branchId: string;
  authorizationProfileId: string;
  onSelect: (part: SupplierCatalogSelection) => void;
};

const formSchema = supplierPartsSearchRequestSchema
  .pick({
    catalogNumber: true,
    manufacturerId: true,
    supplierId: true,
    searchType: true,
  })
  .extend({
    catalogNumber: supplierPartsSearchRequestSchema.shape.catalogNumber.unwrap(),
    searchType: supplierPartsSearchRequestSchema.shape.searchType.removeDefault(),
    manufacturerId: supplierPartsSearchRequestSchema.shape.manufacturerId.nullable(),
    supplierId: supplierPartsSearchRequestSchema.shape.supplierId.nullable(),
  });
type SearchForm = z.infer<typeof formSchema>;

const SUPPLIER_PAGE_SIZE = 100;

/** Scope changes discard submitted queries and their selectable results. */
export function SupplierCatalogPicker(props: SupplierCatalogPickerProps) {
  return (
    <ScopedSupplierCatalogPicker
      key={`${props.branchId}:${props.authorizationProfileId}`}
      {...props}
    />
  );
}

function ScopedSupplierCatalogPicker(props: SupplierCatalogPickerProps) {
  const manufacturers = useGetManufacturersQuery();
  const suppliers = useGetSupplierCustomersQuery();
  const [searchParts, parts] = useLazySearchSupplierPartsQuery();
  const formatCurrency = useFormatCurrency();
  const manufacturerOptions = getOptionsFromManufacturers(manufacturers.data);
  const supplierOptions = getOptionsFromCustomersAsSupplier(suppliers.data);
  const formatPrice = (value: number | null | undefined, currency: string) =>
    isNil(value) ? EMPTY_PLACEHOLDER : formatCurrency(value, currency, 2);
  const offset = parts.originalArgs?.offset ?? 0;
  const nextPage = supplierPartsSearchRequestSchema.safeParse({
    ...parts.originalArgs,
    offset: offset + SUPPLIER_PAGE_SIZE,
  });
  const loadPage = (nextOffset: number) => {
    if (!isNil(parts.originalArgs)) {
      void searchParts({...parts.originalArgs, offset: nextOffset}, false);
    }
  };

  return (
    <VStack spacing={4}>
      <DataStatus
        isLoading={manufacturers.isLoading || suppliers.isLoading}
        isError={manufacturers.isError || suppliers.isError}
      >
        <Form<SearchForm>
          experimentalZodSchema={formSchema}
          defaultValues={{
            catalogNumber: '',
            manufacturerId: null,
            supplierId: null,
            searchType: 'EQUALS',
          }}
          onSubmit={(values) => {
            // A repeated search must read current dealer settings even when RTK has the
            // same catalog number cached. Final warehouse writes still revalidate prices.
            void searchParts(
              {
                ...values,
                manufacturerId: values.manufacturerId ?? undefined,
                supplierId: values.supplierId ?? undefined,
                branchId: props.branchId,
                authorizationProfileId: props.authorizationProfileId,
              },
              false
            );
            return Promise.resolve();
          }}
        >
          {(control) => (
            <VStack spacing={4}>
              <FormField
                name="catalogNumber"
                type="text"
                control={control}
                isRequired
                label={i18n.t('entity.warehouse.labels.catalogueNumber')}
                data-testid={suffixTestId('catalogNumber', props)}
              />
              <FormField
                name="searchType"
                type="choice"
                control={control}
                isNotClearable
                options={getSearchTypeOptions(false)}
                data-testid={suffixTestId('searchType', props)}
              />
              <FormField
                name="manufacturerId"
                type="choice"
                control={control}
                options={manufacturerOptions}
                label={i18n.t('entity.warehouse.labels.manufacturer')}
                data-testid={suffixTestId('manufacturer', props)}
              />
              <FormField
                name="supplierId"
                type="choice"
                control={control}
                options={supplierOptions}
                label={i18n.t('entity.warehouse.labels.supplier')}
                data-testid={suffixTestId('supplier', props)}
              />
              <FormButton
                control={control}
                type="submit"
                title={i18n.t('general.actions.apply')}
                data-testid={suffixTestId('search', props)}
              />
            </VStack>
          )}
        </Form>
      </DataStatus>
      <Show when={!parts.isUninitialized}>
        <DataStatus isLoading={parts.isFetching} isError={parts.isError}>
          <VStack spacing={3}>
            <Text>
              {i18n.t('entity.warehouse.labels.items')}: {parts.currentData?.parts.length ?? 0}
              {parts.currentData?.hasMore ? '+' : ''}
            </Text>
            <Show when={parts.currentData?.parts.length === 0}>
              <Text>{i18n.t('page.warehouseDetail.labels.emptyAssortment')}</Text>
            </Show>
            <HStack spacing={3}>
              <Button
                title={i18n.t('general.actions.back')}
                isDisabled={parts.isFetching || offset === 0}
                onClick={() => loadPage(Math.max(0, offset - SUPPLIER_PAGE_SIZE))}
                data-testid={suffixTestId('previousPage', props)}
              />
              <Button
                title={i18n.t('general.labels.more')}
                isDisabled={parts.isFetching || !parts.currentData?.hasMore || !nextPage.success}
                onClick={() => loadPage(offset + SUPPLIER_PAGE_SIZE)}
                data-testid={suffixTestId('nextPage', props)}
              />
            </HStack>
            <Show when={parts.currentData?.parts}>
              {(parts.currentData?.parts ?? []).map((part) => (
                <Card
                  key={`${part.supplier}:${part.manufacturer}:${part.oemCode}:${part.supplierAccountCode ?? ''}:${part.supplierOrderTypeCode ?? ''}`}
                  title={part.name}
                  data-testid={suffixTestId(`part-${part.oemCode}`, props)}
                >
                  <VStack spacing={3}>
                    <Text>{part.oemCode}</Text>
                    <Text>
                      {manufacturerOptions.find((option) => option.value === part.manufacturer)
                        ?.label ?? part.priceListCode}
                    </Text>
                    <Text>
                      {supplierOptions.find((option) => option.value === part.supplier)?.label ??
                        EMPTY_PLACEHOLDER}
                    </Text>
                    <VStack>
                      <Label>{i18n.t('entity.warehouse.labels.supplierOrderTypeCode')}</Label>
                      <Text>{part.supplierOrderTypeCode ?? EMPTY_PLACEHOLDER}</Text>
                    </VStack>
                    <VStack>
                      <Label>
                        {`${i18n.t('entity.warehouse.labels.supplier')} · ${i18n.t('entity.warehouse.labels.availability')}`}
                      </Label>
                      <Text>
                        {match(part.supplierOrderable)
                          .with(true, () => i18n.t('entity.warehouse.labels.available'))
                          .with(false, () => i18n.t('entity.workshop.labels.notAvailable'))
                          .otherwise(() => EMPTY_PLACEHOLDER)}
                      </Text>
                    </VStack>
                    <HStack spacing={4}>
                      <VStack>
                        <Label>{i18n.t('entity.warehouse.labels.purchasePriceWithoutVat')}</Label>
                        <Text>{formatPrice(part.purchasePrice, part.currency)}</Text>
                      </VStack>
                      <VStack>
                        <Label>
                          {i18n.t('entity.warehouse.labels.recommendedPriceWithoutVat')}
                        </Label>
                        <Text>{formatPrice(part.recommendedSellingPrice, part.currency)}</Text>
                      </VStack>
                    </HStack>
                    <Button
                      title={i18n.t('entity.warehouse.labels.warehouseAvailability')}
                      onClick={() =>
                        props.onSelect({
                          ...part,
                          manufacturerLabel:
                            manufacturerOptions.find((option) => option.value === part.manufacturer)
                              ?.label ?? part.priceListCode,
                          supplierLabel:
                            supplierOptions.find((option) => option.value === part.supplier)
                              ?.label ?? EMPTY_PLACEHOLDER,
                        })
                      }
                      data-testid={suffixTestId(`select-${part.oemCode}`, props)}
                    />
                  </VStack>
                </Card>
              ))}
            </Show>
          </VStack>
        </DataStatus>
      </Show>
    </VStack>
  );
}
